<template>
  <div
    class="v2-wishlists-dropdown"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <button
      class="v2-wishlists-dropdown__toggle"
      :class="{ 'v2-wishlists-dropdown__toggle--center': centerIconsWhenNoLabel && !isBrandShopPage }"
      :disabled="disabled"
      @click.stop="toggleDropdown"
    >
      <div
        v-if="isBrandShopPage"
        class="v2-wishlists-dropdown__label"
      >
        Wishlists
      </div>
      <div class="v2-wishlists-dropdown__toggle-icons">
        <div
          class="v2-wishlists-dropdown__heart"
          :class="{ 'v2-wishlists-dropdown__heart--filled': productWishlistsCount }"
        >
          <!-- Heart icon -->
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            :fill="productWishlistsCount ? 'currentColor' : 'none'"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
          <span
            v-if="productWishlistsCount"
            class="v2-wishlists-dropdown__count"
          >
            {{ productWishlistsCount }}
          </span>
        </div>
        <!-- Chevron -->
        <svg
          class="v2-wishlists-dropdown__chevron"
          :class="{ 'v2-wishlists-dropdown__chevron--open': isOpen }"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
    </button>

    <!-- Dropdown menu -->
    <div
      v-if="isOpen"
      class="v2-wishlists-dropdown__menu"
    >
      <!-- My wishlists -->
      <template
        v-if="productWishlistsCreatedByRetailer && productWishlistsCreatedByRetailer.length"
      >
        <div
          v-if="!isSelections"
          class="v2-wishlists-dropdown__header"
        >
          My wishlists
        </div>
        <button
          v-for="wishlist in productWishlistsCreatedByRetailer"
          :key="'r-' + wishlist._id"
          class="v2-wishlists-dropdown__item"
          :disabled="getDisabled(product, wishlist)"
          @click.stop="onSelectWishlistOnProduct(wishlist)"
        >
          <span class="v2-wishlists-dropdown__tick">
            <svg
              v-if="resolvedProductWishlists.includes(wishlist._id) || forceShowCheckIcons"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="v2-wishlists-dropdown__check-icon"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
          <span class="v2-wishlists-dropdown__item-name">{{ wishlist.name }}</span>
        </button>
      </template>

      <!-- Shared by brand -->
      <template
        v-if="productWishlistsSharedByBrand && productWishlistsSharedByBrand.length"
      >
        <div
          v-if="!isSelections"
          class="v2-wishlists-dropdown__header"
        >
          Shared by brand
        </div>
        <button
          v-for="wishlist in productWishlistsSharedByBrand"
          :key="'b-' + wishlist._id"
          class="v2-wishlists-dropdown__item"
          :disabled="getDisabled(product, wishlist)"
          @click.stop="onSelectWishlistOnProduct(wishlist)"
        >
          <span class="v2-wishlists-dropdown__tick">
            <svg
              v-if="resolvedProductWishlists.includes(wishlist._id) || forceShowCheckIcons"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="v2-wishlists-dropdown__check-icon"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
          <span class="v2-wishlists-dropdown__item-name">{{ wishlist.name }}</span>
        </button>
      </template>

      <!-- Create wishlist/selection -->
      <button
        class="v2-wishlists-dropdown__item v2-wishlists-dropdown__item--create"
        @click.stop="createWishlist()"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line
            x1="12"
            y1="5"
            x2="12"
            y2="19"
          />
          <line
            x1="5"
            y1="12"
            x2="19"
            y2="12"
          />
        </svg>
        <span>Create {{ getWishlistTitle().toLowerCase() }}</span>
      </button>
    </div>

    <!--
      Outside-click is now handled by a document listener (see mounted /
      handleOutsideClick) so a single click on another card's chevron can
      close this one and open that one in the same gesture. A fixed
      backdrop here would intercept that click and force a two-click
      switch.
    -->
  </div>
</template>

<script>
import analytics from '@/@core/utils/analytics'
import { getWishlistTitle } from '@/@core/utils/utils'
import constants from '@/constants'
import { SET_IS_NO_HOVER_PRODUCT_CARD } from '@/store/modules/shop.module'
import UserRoleMixin from '@/views/apps/UserRoleMixin.vue'

const { PRODUCTS_PAGE_WISHLISTS } = constants.TRACKS.ACTIONS

export default {
  name: 'V2ProductWishlistsDropdown',
  mixins: [UserRoleMixin],
  props: {
    product: {
      type: Object,
      required: true,
    },
    showCheckBox: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    wishlists: {
      type: Array,
      default: () => [],
    },
    isSelections: {
      type: Boolean,
      default: false,
    },
    productWishlistsCount: {
      type: Number,
      default: 0,
    },
    productWishlistsIds: {
      type: Array,
      default: () => [],
    },
    forceShowCheckIcons: {
      type: Boolean,
      default: false,
    },
    centerIconsWhenNoLabel: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      isActiveDropdown: false,
      productWishlistsCreatedByRetailer: [],
      productWishlistsSharedByBrand: [],
      getWishlistTitle,
    }
  },
  computed: {
    isBrandShopPage() {
      return this.isSelections
    },
    pageType() {
      return 'Collection products page'
    },
    resolvedProductWishlists() {
      // Prefer explicit prop (client-side map), fall back to product data
      if (this.productWishlistsIds && this.productWishlistsIds.length) {
        return this.productWishlistsIds
      }
      return this.product.wishlists || []
    },
  },
  watch: {
    wishlists: {
      handler() {
        this.setWishlistsForTheDropdown()
      },
      immediate: true,
    },
  },
  mounted() {
    // Outside-click closes this dropdown.
    document.addEventListener('mousedown', this.handleOutsideClick)
    // Exclusive-open coordination: when any other instance opens, close
    // this one. Uses Vue's $root as a lightweight bus so we don't need to
    // pull in Vuex state for what is purely UI coordination.
    this.$root.$on('v2-wishlists-dropdown:opened', this.handleOtherOpened)
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleOutsideClick)
    this.$root.$off('v2-wishlists-dropdown:opened', this.handleOtherOpened)
  },
  methods: {
    toggleDropdown() {
      if (this.isOpen) {
        this.closeDropdown()
      } else {
        this.openDropdown()
      }
    },
    openDropdown() {
      this.isOpen = true
      this.onToggleWishlistDropdown(true)
      // Tell any other open instance to close itself.
      this.$root.$emit('v2-wishlists-dropdown:opened', this._uid)
      this.$nextTick(() => {
        const menu = this.$el.querySelector('.v2-wishlists-dropdown__menu')
        if (menu) {
          menu.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    },
    closeDropdown() {
      this.isOpen = false
      this.onToggleWishlistDropdown(false)
    },
    handleOutsideClick(e) {
      if (!this.isOpen) return
      // $el wraps the toggle + menu, so any click within the toggle or
      // menu items keeps the dropdown open. Clicks elsewhere close it.
      if (!this.$el.contains(e.target)) {
        this.closeDropdown()
      }
    },
    handleOtherOpened(uid) {
      if (uid !== this._uid && this.isOpen) {
        this.closeDropdown()
      }
    },
    getDisabled(product, wishlist) {
      return !!product?.idsDisabledToSelect?.includes(wishlist._id)
    },
    onMouseEnter() {
      this.isActiveDropdown = true
      this.$store.commit(SET_IS_NO_HOVER_PRODUCT_CARD, true)
    },
    onMouseLeave() {
      this.isActiveDropdown = false
      if (!this.isOpen) {
        this.$store.commit(SET_IS_NO_HOVER_PRODUCT_CARD, false)
      }
    },
    onSelectWishlistOnProduct(wishlist) {
      this.$emit('select-wishlist-on-product', wishlist)
      const action = this.resolvedProductWishlists.includes(wishlist._id)
        ? 'REMOVED_PRODUCT_FROM'
        : 'ADDED_PRODUCT_TO'
      const wishlistType = this.isBrand ? 'SELECTION' : 'WISHLIST'
      const role = this.isBrand ? 'BRAND' : 'RETAILER'
      const source = this.isRetailer && wishlist.isBrandInitiated
        ? `SHARED_${wishlistType}`
        : `MY_${wishlistType}`
      const trackEvent = `${role}_${action}_A_${wishlistType}_BY_CLICKING_A_${source}_IN_THE_${wishlistType}S_DROPDOWN`

      analytics.track(PRODUCTS_PAGE_WISHLISTS[trackEvent], {
        product: this.product,
        wishlist,
        pageType: this.pageType,
      })
      this.closeDropdown()
    },
    createWishlist() {
      this.$emit('create-wishlist')
      const trackEvent = this.isBrand
        ? PRODUCTS_PAGE_WISHLISTS.BRAND_CLICKS_CREATE_SELECTION_IN_THE_SELECTIONS_DROPDOWN
        : PRODUCTS_PAGE_WISHLISTS.RETAILER_CLICKS_CREATE_WISHLIST_IN_THE_WISHLISTS_DROPDOWN
      analytics.track(trackEvent, {
        product: this.product,
        pageType: this.pageType,
      })
      this.closeDropdown()
    },
    onToggleWishlistDropdown(isOpened = false) {
      this.$emit('toggle-wishlist-dropdown', isOpened)
      if (isOpened) {
        const trackEvent = this.isBrand
          ? PRODUCTS_PAGE_WISHLISTS.BRAND_OPENS_THE_SELECTIONS_DROPDOWN_BY_CLICKING_ON_THE_HEART_ICON
          : PRODUCTS_PAGE_WISHLISTS.RETAILER_OPENS_THE_WISHLISTS_DROPDOWN_BY_CLICKING_ON_THE_HEART_ICON
        analytics.track(trackEvent, {
          product: this.product,
          pageType: this.pageType,
        })
      } else {
        const trackEvent = this.isBrand
          ? PRODUCTS_PAGE_WISHLISTS.BRAND_CLOSES_THE_SELECTIONS_DROPDOWN
          : PRODUCTS_PAGE_WISHLISTS.RETAILER_CLOSES_THE_WISHLISTS_DROPDOWN
        analytics.track(trackEvent, {
          product: this.product,
          pageType: this.pageType,
        })
        if (!this.isActiveDropdown) {
          this.$store.commit(SET_IS_NO_HOVER_PRODUCT_CARD, false)
        }
      }
    },
    setWishlistsForTheDropdown() {
      if (this.isSelections) {
        // For brands: show all wishlists as selections (no split)
        this.productWishlistsSharedByBrand = this.wishlists
      } else {
        // For retailers: split into own wishlists vs brand-shared
        this.productWishlistsCreatedByRetailer = this.wishlists.filter(
          wishlist => !wishlist?.isBrandInitiated,
        )
        this.productWishlistsSharedByBrand = this.wishlists.filter(
          wishlist => wishlist?.isBrandInitiated,
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-wishlists-dropdown {
  position: relative;

  &__toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: var(--v2-space-2) var(--v2-space-3);
    min-height: 42px;
    background: none;
    border: none;
    border-top: 1px solid var(--v2-border);
    cursor: pointer;
    color: var(--v2-foreground);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    transition: background 0.15s ease;

    &:hover {
      background: var(--v2-muted);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--center {
      justify-content: center;

      .v2-wishlists-dropdown__heart {
        margin-left: 0;
      }
    }
  }

  &__label {
    font-weight: 600;
    color: var(--v2-foreground);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__toggle-icons {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
  }

  &__heart {
    position: relative;
    display: flex;
    align-items: center;
    margin-left: var(--v2-space-2);

    &--filled {
      color: var(--v2-destructive);
    }
  }

  &__count {
    position: absolute;
    bottom: -2px;
    right: -8px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    border-radius: 999px;
    background: var(--v2-primary);
    color: var(--v2-primary-foreground);
    font-size: 0.625rem;
    font-weight: 700;
    border: 2px solid var(--v2-card);
  }

  &__chevron {
    margin-left: var(--v2-space-1);
    transition: transform 0.2s ease;
    flex-shrink: 0;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 50;
    background: var(--v2-card);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    box-shadow: var(--v2-shadow-md);
    max-height: 280px;
    overflow-y: auto;
    margin-top: var(--v2-space-1);
  }

  &__header {
    padding: var(--v2-space-2) var(--v2-space-3);
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--v2-muted-foreground);
    background: var(--v2-muted);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__item {
    display: flex;
    align-items: center;
    width: 100%;
    padding: var(--v2-space-2) var(--v2-space-3);
    background: none;
    border: none;
    cursor: pointer;
    font-family: var(--v2-font-sans);
    font-size: 0.8125rem;
    color: var(--v2-foreground);
    text-align: left;
    transition: background 0.1s ease;

    &:hover {
      background: var(--v2-muted);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    &--create {
      border-top: 1px solid var(--v2-border);
      color: var(--v2-primary);
      font-weight: 500;
      gap: var(--v2-space-2);
    }
  }

  &__tick {
    width: 20px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }

  &__check-icon {
    color: var(--v2-success);
  }

  &__item-name {
    overflow-wrap: break-word;
    word-break: break-word;
  }

}
</style>
